import { ErrorAlert, LoadingAlert, Table, WarningAlert } from '@/components';
import {
  TableCellProps,
  TableRowProps,
} from '@/components/molecules/Table/interfaces/Table';
import TransactionDetailTray from '@/components/organisms/TransactionDetail/TransactionDetailTray';
import { useTransactions } from '@/components/providers/postauth/UserTransactionsProvider';
import { useWalletsContext } from '@/components/providers/postauth/UserWalletsProvider';
import { TokenInterface } from '@/interfaces';
import { TransactionInterface } from '@/interfaces/TransactionInterface';
import { getColorBasedOnState } from '@/utils/colorUtils';
import {
  formatDate,
  formatNumberToAmountInTokenCurrencyString,
  formatNumberToAmountInUsdString,
  formatToSentenceCase,
} from '@/utils/formatUtils';
import {
  getDirectionAdjustedAmountInUSDFromTransaction,
  getDirectionAdjustedFeeAmountInUSDFromTransaction,
  getTransactionTypeDetails,
} from '@/utils/functionalUtils';
import { logError, logInfo, logWarning } from '@/utils/logger';
import { TRANSACTIONS, WALLET } from '@/utils/strings';
import { FC, useMemo, useState } from 'react';
export enum InformationAmount {
  LEAST_INFORMATION = 'least_information',
  FULL_INFORMATION = 'full_information',
}
interface TransactionFilters {
  yieldBearingAssetId?: string;
  limit?: number;
  type?: string;
  status?: string;
  startDate?: string;
  endDate?: string;
  minAmount?: number;
  maxAmount?: number;
  tokens?: TokenInterface[];
}
interface TransactionsListProps {
  filters?: TransactionFilters;
  onRowClick?: (transaction: TransactionInterface) => void;
  amountOfInformationToShow?: InformationAmount;
}
const TransactionsList: FC<TransactionsListProps> = ({
  filters = {},
  onRowClick,
  amountOfInformationToShow = InformationAmount.FULL_INFORMATION,
}) => {
  const {
    transactions,
    isLoading: loading,
    error,
    isUserAuthorized,
  } = useTransactions();
  const { wallets } = useWalletsContext();
  const [sortedColumn, setSortedColumn] = useState<number>(-1);
  const [isAscending, setIsAscending] = useState<boolean>(true);
  const [isTrayOpen, setIsTrayOpen] = useState<boolean>(false);
  const [selectedTransaction, setSelectedTransaction] =
    useState<TransactionInterface | null>(null);
  const walletAddresses = useMemo(
    () => wallets.map((wallet) => wallet.address),
    [wallets],
  );
  const filteredTransactions = useMemo(() => {
    return transactions
      .filter((transaction) => {
        const matchesType = !filters.type || transaction.type === filters.type;
        const matchesStatus =
          !filters.status || transaction.status === filters.status;
        const matchesStartDate =
          !filters.startDate ||
          new Date(transaction.createdAt) >= new Date(filters.startDate);
        const matchesEndDate =
          !filters.endDate ||
          new Date(transaction.createdAt) <= new Date(filters.endDate);
        const matchesMinAmount =
          !filters.minAmount ||
          parseFloat(transaction.amount) >= filters.minAmount;
        const matchesMaxAmount =
          !filters.maxAmount ||
          parseFloat(transaction.amount) <= filters.maxAmount;
        const matchesYieldBearingAsset =
          !filters.yieldBearingAssetId ||
          transaction.yieldBearingAsset?.ticker === filters.yieldBearingAssetId;
        const matchesTokenInterfaces =
          !filters.tokens ||
          filters.tokens.length === 0 ||
          (transaction.sellAsset &&
            filters.tokens.some(
              (token) => token.id === transaction.sellAsset!.id,
            )) ||
          (transaction.buyAsset &&
            filters.tokens.some(
              (token) => token.id === transaction.buyAsset!.id,
            ));
        return (
          matchesType &&
          matchesStatus &&
          matchesStartDate &&
          matchesEndDate &&
          matchesMinAmount &&
          matchesMaxAmount &&
          matchesYieldBearingAsset &&
          matchesTokenInterfaces
        );
      })
      .sort(
        (a, b) =>
          new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
      );
  }, [
    transactions,
    filters.type,
    filters.status,
    filters.startDate,
    filters.endDate,
    filters.minAmount,
    filters.maxAmount,
    filters.yieldBearingAssetId,
    filters.tokens,
  ]);
  const limitedTransactions = useMemo(() => {
    if (filters.limit) {
      return filteredTransactions.slice(0, filters.limit);
    }
    return filteredTransactions;
  }, [filteredTransactions, filters.limit]);
  if (!isUserAuthorized) {
    logError('User is not authorized to use Sliver', 'TransactionsList');
    return (
      <ErrorAlert
        title={WALLET.UNAUTHORIZED_USER_ERROR_TITLE}
        description={WALLET.UNAUTHORIZED_USER_ERROR_DESCRIPTION}
      />
    );
  }
  if (!loading && !error && limitedTransactions.length === 0) {
    logWarning('No transactions match current filters', 'TransactionsList');
    return (
      <WarningAlert
        title={TRANSACTIONS.FILTERED_TRANSACTIONS_NOT_FOUND_TITLE}
        description={TRANSACTIONS.FILTERED_TRANSACTIONS_NOT_FOUND_DESCRIPTION}
      />
    );
  }
  if (loading && transactions.length === 0) {
    logInfo('Loading transactions, but none found yet', 'TransactionsList');
    return (
      <LoadingAlert
        title={TRANSACTIONS.TRANSACTIONS_NOT_FOUND_TITLE}
        description={TRANSACTIONS.TRANSACTIONS_NOT_FOUND_DESCRIPTION}
      />
    );
  }
  if (error) {
    logError('Error loading transactions', 'TransactionsList', { error });
  }
  const handleSort = (columnIndex: number) => {
    if (sortedColumn === columnIndex) {
      setIsAscending(!isAscending);
    } else {
      setSortedColumn(columnIndex);
      setIsAscending(true);
    }
  };
  const getComparableValue = (
    transaction: TransactionInterface,
    columnIndex: number,
  ): string | number => {
    switch (columnIndex) {
      case 0:
        return getTransactionTypeDetails({
          transaction,
          walletAddresses,
        }).mainText;
      case 1:
        return getDirectionAdjustedAmountInUSDFromTransaction({ transaction });
      case 2:
        return getDirectionAdjustedFeeAmountInUSDFromTransaction({
          transaction,
        });
      case 3:
        return transaction.status;
      case 4:
        return new Date(transaction.updatedAt).getTime();
      default:
        return '';
    }
  };
  const sortedTransactions =
    sortedColumn !== -1
      ? [...limitedTransactions].sort((a, b) => {
          const aValue = getComparableValue(a, sortedColumn);
          const bValue = getComparableValue(b, sortedColumn);
          if (typeof aValue === 'string' && typeof bValue === 'string') {
            return isAscending
              ? aValue.localeCompare(bValue)
              : bValue.localeCompare(aValue);
          } else {
            return isAscending
              ? Number(aValue) - Number(bValue)
              : Number(bValue) - Number(aValue);
          }
        })
      : limitedTransactions;
  const handleRowClick = (transaction: TransactionInterface) => {
    if (onRowClick) {
      onRowClick(transaction);
    } else {
      setSelectedTransaction(transaction);
      setIsTrayOpen(true);
    }
  };
  const rows: TableRowProps[] = sortedTransactions.map((transaction) => {
    const { mainText, subText, iconPng, icon } = getTransactionTypeDetails({
      transaction,
      walletAddresses,
    });
    const stateColor = getColorBasedOnState(transaction.status);
    const cells: TableCellProps[] = [
      {
        mainText,
        subText,
        leftIcon: icon,
        leftIconWidth: 36,
        leftIconHeight: 16,
      },
      {
        mainText: formatNumberToAmountInTokenCurrencyString(
          parseFloat(transaction.amount),
          transaction.asset?.symbol || transaction.sellAsset?.symbol || '',
        ),
        subText: formatNumberToAmountInUsdString(
          parseFloat(transaction.amountInUsd),
        ),
      },
      ...(amountOfInformationToShow === InformationAmount.FULL_INFORMATION
        ? [
            {
              mainText: formatNumberToAmountInTokenCurrencyString(
                parseFloat(transaction.networkFee),
                transaction.asset?.symbol || '',
              ),
              subText: formatNumberToAmountInUsdString(
                parseFloat(transaction.networkFeeInUsd),
              ),
            },
          ]
        : []),
      {
        mainText: formatToSentenceCase(transaction.status),
        subText:
          amountOfInformationToShow === InformationAmount.LEAST_INFORMATION
            ? formatDate(transaction.updatedAt)
            : '',
        mainTextColor: stateColor,
        subTextColor: stateColor,
      },
      ...(amountOfInformationToShow === InformationAmount.FULL_INFORMATION
        ? [
            {
              mainText: formatDate(transaction.updatedAt),
            },
          ]
        : []),
    ];
    return { cells };
  });
  return (
    <>
      <Table
        rightAlignLastColumn={
          amountOfInformationToShow === InformationAmount.LEAST_INFORMATION
        }
        columnHeaders={[
          'Transaction',
          'Amount',
          ...(amountOfInformationToShow === InformationAmount.FULL_INFORMATION
            ? ['Network Fee']
            : []),
          'Status',
          ...(amountOfInformationToShow === InformationAmount.FULL_INFORMATION
            ? ['Date']
            : []),
        ]}
        columnWidths={
          amountOfInformationToShow === InformationAmount.FULL_INFORMATION
            ? [30, 20, 15, 10, 25]
            : [50, 20, 30]
        }
        rows={rows}
        sortable={
          amountOfInformationToShow === InformationAmount.FULL_INFORMATION
            ? [true, true, true, true, true]
            : [true, true, true]
        }
        onSort={handleSort}
        sortedColumn={sortedColumn}
        isAscending={isAscending}
        onClickRow={(event, rowIndex) =>
          handleRowClick(sortedTransactions[rowIndex])
        }
      />
      {selectedTransaction && (
        <TransactionDetailTray
          isTrayOpen={isTrayOpen}
          setIsTrayOpen={setIsTrayOpen}
          transaction={selectedTransaction}
        />
      )}
    </>
  );
};
export default TransactionsList;
